import {
    bexHttpClient
} from '../../clients';
import {
    projectOService
} from './projectOProxy.service';
import {
    CreateOpcodeAssistantRequest,
    UpdateOpcodeAssistantRequest,
    DeleteOpcodeAssistantRequest,
    CreateOpcodeAssistantResponse,
    UpdateOpcodeAssistantResponse,
    GetOpcodeAssistantResponse,
    ListOpcodeAssistantsResponse,
    DeleteOpcodeAssistantResponse
} from '../bexService.types';

/**
 * Service for the rich assistant CRUD + memory management.
 *
 * Payload shape (rich): see `builderAssistant.types.ts::Assistant`.
 * Server endpoints are at `/cm/redis/opcode-assistants/*` (bex client prefixes
 * with the cm base; the client passes `/redis/opcode-assistants/...`).
 *
 * A-0007.03 Addendum (2026-04-24):
 *   - CREATE: send `title` (and the rest of the rich shape). MUST NOT include
 *     `assistant_id` — Server auto-generates the slug and returns it on the
 *     response body. UI uses that returned id for routing + subsequent calls.
 *   - UPDATE / GET / DELETE: `assistantId` path/query param = the slug.
 *   - Schema fields: `opcodes[]` is always the rich-object array; there is
 *     no `opcodes_v2`, no legacy `_id`, no `assistant_name`.
 */
class OpcodeAssistantService {

    /**
     * Fetch one assistant by id.
     */
    async getOpcodeAssistant(
        organizationId: string,
        subOrganizationId: string,
        assistantId: string,
        options?: { enrichOpcodes?: boolean },
    ): Promise<GetOpcodeAssistantResponse> {
        const enrich = options?.enrichOpcodes !== false ? '&enrich=opcodes' : '';
        const response = await bexHttpClient.get(
            `/redis/opcode-assistants/get-assistant/${encodeURIComponent(assistantId)}?organizationId=${encodeURIComponent(organizationId)}&subOrganizationId=${encodeURIComponent(subOrganizationId)}${enrich}`
        );
        return response;
    }

    /**
     * List assistants for an org + sub-org.
     * Defaults to enrich=opcodes so linked opcode titles are included.
     */
    async listOpcodeAssistants(
        organizationId: string,
        subOrganizationId: string,
        page?: number,
        limit?: number,
    ): Promise<ListOpcodeAssistantsResponse> {
        let url = `/redis/opcode-assistants/list-assistants?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}`;
        if (page !== undefined) url += `&page=${page}`;
        if (limit !== undefined) url += `&limit=${limit}`;
        const response = await bexHttpClient.get(url);
        return response;
    }

    /**
     * Create assistant (rich shape). Server applies Joi defaults for every
     * missing optional field — caller must supply `title`, `org_id`,
     * `sub_org_id`, `assistant_token`. Server auto-generates `assistant_id`
     * from `title` and returns it on the response body.
     * Do NOT send `assistant_id` in the body — Joi will reject it.
     */
    async createOpcodeAssistant(data: CreateOpcodeAssistantRequest): Promise<CreateOpcodeAssistantResponse> {
        const response = await bexHttpClient.post('/redis/opcode-assistants/create-assistant', data);
        return response;
    }

    /**
     * Full-doc update. Server merges over the existing doc, preserves
     * `created`/`createdBy`, stamps `updated`/`updatedBy`. Server strips
     * the read-only audit fields, so round-tripping a GET result is safe.
     */
    async updateOpcodeAssistant(data: UpdateOpcodeAssistantRequest): Promise<UpdateOpcodeAssistantResponse> {
        // Addendum §A: assistantId is a path param. Strip it from the body so
        // Joi doesn't see it twice (harmless, but cleaner).
        const {
 assistantId, ...body
} = data;
        const response = await bexHttpClient.put(
            `/redis/opcode-assistants/update-assistant/${encodeURIComponent(assistantId)}`,
            body,
        );
        return response;
    }

    /**
     * Delete assistant. Cascades: every channel whose `assistantId` matches
     * is removed from common Redis (TRK-035).
     */
    async deleteOpcodeAssistant(data: DeleteOpcodeAssistantRequest): Promise<DeleteOpcodeAssistantResponse> {
        // Addendum §A: assistantId is a path param; org_id/sub_org_id stay in body.
        const {
 assistantId, ...body
} = data;
        const response = await bexHttpClient.delete(
            `/redis/opcode-assistants/delete-assistant/${encodeURIComponent(assistantId)}`,
            {
 data: body
},
        );
        return response;
    }

    /**
     * Clear one conversation's memory. Proxies to Mind's internal endpoint.
     * Used by the builder UI + GDPR delete flows.
     */
    async clearConversationMemory(
        assistantId: string,
        memoryLogId: string,
        body: { org_id: string; sub_org_id: string }
    ): Promise<{ data: { deleted: boolean } }> {
        const response = await bexHttpClient.delete(
            `/redis/opcode-assistants/assistants/${assistantId}/memory/conversations/${memoryLogId}`,
            {
 data: body
}
        );
        return response;
    }

    /**
     * Clear the employee-scope memory for an assistant. Proxies to Mind.
     */
    async clearEmployeeMemory(
        assistantId: string,
        body: { org_id: string; sub_org_id: string }
    ): Promise<{ data: { deleted: boolean } }> {
        const response = await bexHttpClient.delete(
            `/redis/opcode-assistants/assistants/${assistantId}/memory/employee`,
            {
 data: body
}
        );
        return response;
    }

    /**
     * Fetch function registry (unchanged — still used for OpCode step config).
     */
    async getFunctionRegistry(): Promise<any> {
        const response = await projectOService.performRequest('projecto/config/api/v1/opcode/get_function_registry', {
            method: 'GET',
        });
        return response;
    }

}

const opcodeAssistantService = new OpcodeAssistantService();
export { opcodeAssistantService };
